import { useEffect, useState } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  setAlert,
  updateFloatPanelData,
  updateFloatPanel,
} from "../../../../../reducers/userConfigSlice";

// Components
import { FloatPanel } from "../../../../../components/floatPanel";
import { OrderReference } from "./reference";
import "./reference.css";

// API
import {
  addProductToPendingOrder,
  getOrderDetails,
  removeProductToPendingOrder,
  updatePendingOrder,
  updateQuantityProductToPendingOrder,
  createInvoice,
  printInvoice,
  printTicket,
} from "../../../../../api/orders";
import { getProducts, getProduct } from "../../../../../api/products";
import { getClients } from "../../../../../api/clients";

// Utils
import { Order, translateMessage } from "@delibrandev/utils";

const editionView = (order) =>
  order
    ? {
        ...order,
        ...(order.displayNumber
          ? {
              number: order.displayNumber,
              SalesChannel: { ...order.SalesChannel, identifier: "" },
            }
          : {}),
        products: (order.products || []).map((product) => ({
          ...product,
          price: product.unitPrice ?? product.price,
        })),
      }
    : order;

export const OrderComponent = ({ order, onDataChange }) => {
  const dispatch = useDispatch();
  const data = order;
  const variants = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.data?.variants,
  );
  const atributos = useSelector(
    (state) => state.aplicationConfig.corporationData.attributes,
  );

  // Query global to get data by id
  const queryGetById = async (id, endpoint) => {
    try {
      if (!id) return null;

      const response = await endpoint(id);
      if (response && !response.error) {
        return response.data || null;
      }
      showAlert(response?.message);
      return null;
    } catch (error) {
      showAlert("Error");
      return null;
    }
  };

  // Query global to get data
  const queryGet = async (endpoint) => {
    try {
      const response = await endpoint();
      if (response && !response.error) {
        return response.data || [];
      }

      showAlert(response?.message);
      return [];
    } catch (error) {
      showAlert("Error");
      return [];
    }
  };

  // Query global for update order
  const queryOrder = async (payload, endpoint) => {
    try {
      if (!data?.orderId) return null;

      const response = await endpoint(payload, data.orderId);
      if (response && !response.error) {
        showAlert(response?.message || "Correcto");
        const freshOrder = await queryGetById(data.orderId, getOrderDetails);
        if (freshOrder) updateOrderPanelData(freshOrder);
        return editionView(freshOrder);
      }
      showAlert(response?.message);
      return null;
    } catch (error) {
      showAlert("Error");
      return null;
    }
  };

  // Query global for docs
  const payload = {
    orderId: data?.orderId,
    isCorrective: false,
    paymentMethods: data?.paymentMethod,
    products: data?.products,
  };

  const queryDocs = async (endpoint) => {
    try {
      const response = await endpoint(payload);
      if (response?.error !== false) throw Error();
      return response;
    } catch (error) {
      throw Error("No se pudo completar la acción del documento.");
    }
  };

  const showAlert = (message) => {
    dispatch(setAlert(translateMessage(message || "Bad request")));
  };

  const updateOrderPanelData = (nextData) => {
    if (!nextData) return;
    onDataChange?.(nextData);
    dispatch(
      updateFloatPanel({
        visible: true,
        queryId: nextData.orderId,
        isEmpty: false,
        data: nextData,
      }),
    );
  };

  const onRefresh = async () => {
    if (!data?.orderId) return null;

    const freshOrder = await queryGetById(data.orderId, getOrderDetails);
    if (freshOrder) updateOrderPanelData(freshOrder);
    return editionView(freshOrder);
  };

  const canCreateInvoice = true;
  const canEdit = true;

  return (
    <OrderReference
      data={data}
      printTicket={() => queryDocs(printTicket)}
      createInvoice={() => queryDocs(createInvoice)}
      printInvoice={() => queryDocs(printInvoice)}
      refresh={onRefresh}
      actions={
        <Order
          data={editionView(data)}
          info={false}
          details={false}
          showProducts={false}
          notes={false}
          resume={false}
          timeline={false}
          canEdit={canEdit}
          edition={{
            clients: [],
            products: [],
            fetchOrderDetails: async (orderId) =>
              editionView(await queryGetById(orderId, getOrderDetails)),
            fetchClients: () => queryGet(getClients),
            fetchProducts: () => queryGet(getProducts),
            fetchProductDetails: (productId) =>
              queryGetById(productId, getProduct),
            onUpdateOrder: (payload) => queryOrder(payload, updatePendingOrder),
            onAddProduct: (payload) =>
              queryOrder(payload, addProductToPendingOrder),
            onRemoveProduct: (payload) =>
              queryOrder(payload, removeProductToPendingOrder),
            onUpdateProductQuantity: (payload) =>
              queryOrder(payload, updateQuantityProductToPendingOrder),
            onShowAlert: showAlert,
            onRefresh,
            onUpdatePanelData: async () => {
              const fresh = await queryGetById(data.orderId, getOrderDetails);
              updateOrderPanelData(fresh);
            },
          }}
          resumeCallbacks={{
            callbackPrintTicket: () => queryDocs(printTicket),
            callbackCreateInvoice: () => queryDocs(createInvoice),
            callbackPrintInvoice: () => queryDocs(printInvoice),
            canCreateInvoice,
          }}
          variants={variants}
          atributos={atributos}
        />
      }
    />
  );
};

export const Edition = () => {
  const panel = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel,
  );
  return (
    <FloatPanel
      className="orderReferencePanel"
      template={<OrderReader key={panel.queryId} id={panel.queryId} />}
    />
  );
};
export const OrderReader = ({ id }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null),
    [error, setError] = useState(false),
    [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    setData(null);
    setError(false);
    Promise.resolve()
      .then(() => getOrderDetails(id))
      .then((response) => {
        if (!current) return;
        if (
          response?.error !== false ||
          !response.data ||
          String(response.data.orderId) !== String(id)
        )
          throw Error();
        setData(response.data);
        dispatch(updateFloatPanelData({ data: response.data }));
      })
      .catch(() => {
        if (current) setError(true);
      });
    return () => {
      current = false;
    };
  }, [id, attempt, dispatch]);
  return data ? (
    <OrderComponent order={data} onDataChange={setData} />
  ) : (
    <div className="orderReference">
      <button
        type="button"
        className="orderBack"
        onClick={() =>
          dispatch(
            updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
          )
        }
      >
        Volver a pedidos
      </button>
      <h2 id="admin-editor-title">Detalles del pedido</h2>
      {error ? (
        <p role="alert">
          No se pudo cargar este pedido.{" "}
          <button type="button" onClick={() => setAttempt((v) => v + 1)}>
            Reintentar
          </button>
        </p>
      ) : (
        <p role="status">Cargando pedido…</p>
      )}
    </div>
  );
};
